import { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import { hasActiveTeamBranding, useActiveTeamBranding } from './teamBrandingEntitlement';

const DEFAULT_BRAND = Object.freeze({
  active: false,
  name: '小影',
  englishName: 'Xiaoying',
  logoUrl: '',
  wordmarkUrl: '',
  faviconUrl: '',
  text: value => value,
});

const TeamBrandContext = createContext(DEFAULT_BRAND);

function normalizeBrandName(team) {
  return String(team?.brand_name || team?.name || '').trim();
}

export function replaceBrandText(value, brandName) {
  if (typeof value !== 'string' || !brandName) return value;
  return value.replace(/Xiaoying(?![\w.-])|小影/gi, () => brandName);
}

export function replaceBrandValue(value, brandName) {
  if (typeof value === 'string') return replaceBrandText(value, brandName);
  if (Array.isArray(value)) return value.map(item => replaceBrandValue(item, brandName));
  if (value && Object.getPrototypeOf(value) === Object.prototype) {
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, replaceBrandValue(item, brandName)]));
  }
  return value;
}

export function resolveTeamBrand(user, team) {
  const name = normalizeBrandName(team);
  if (!user || !hasActiveTeamBranding(team) || !name) return DEFAULT_BRAND;
  return {
    active: true,
    name,
    englishName: name,
    logoUrl: String(team?.brand_logo_url || '').trim(),
    wordmarkUrl: String(team?.brand_wordmark_url || '').trim(),
    faviconUrl: String(team?.brand_favicon_url || '').trim(),
  };
}

export function TeamBrandProvider({ user, team, publicTeam = null, children }) {
  const active = useActiveTeamBranding(publicTeam?.team_id ? publicTeam : team);
  const resolved = useMemo(() => !active ? DEFAULT_BRAND : publicTeam?.team_id
    ? resolveTeamBrand({ publicBrand: true }, publicTeam)
    : resolveTeamBrand(user, team), [active, publicTeam, team, user]);
  const text = useCallback(value => (
    resolved.active ? replaceBrandText(value, resolved.name) : value
  ), [resolved.active, resolved.name]);
  const value = useMemo(() => ({ ...resolved, text }), [resolved, text]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const originalTitle = document.title;
    const icons = Array.from(document.querySelectorAll('link[rel~="icon"], link[rel="apple-touch-icon"]'));
    const originalIcons = icons.map(icon => ({ icon, href: icon.getAttribute('href'), type: icon.getAttribute('type') }));
    const description = document.querySelector('meta[name="description"]');
    const originalDescription = description?.getAttribute('content');
    const manifest = document.querySelector('link[rel="manifest"]');
    const originalManifest = manifest?.getAttribute('href');
    const controller = new AbortController();
    let manifestUrl = '';
    let disposed = false;

    if (resolved.active) {
      document.title = replaceBrandText(originalTitle || '小影创作智能体', resolved.name);
      if (description && originalDescription) description.setAttribute('content', replaceBrandText(originalDescription, resolved.name));
      const iconUrl = resolved.faviconUrl || resolved.logoUrl;
      if (iconUrl) icons.forEach(icon => {
        icon.setAttribute('href', iconUrl);
        // The platform icon is SVG; a custom PNG/WebP must not retain that MIME hint.
        icon.removeAttribute('type');
      });
      if (manifest && originalManifest && typeof fetch === 'function' && URL.createObjectURL) {
        const manifestSource = new URL(originalManifest, window.location.href);
        fetch(manifestSource.href, { credentials: 'omit', signal: controller.signal })
          .then(response => response.ok ? response.json() : null)
          .then(base => {
            if (disposed || !base) return;
            const customIcons = [];
            if (resolved.logoUrl) customIcons.push({ src: resolved.logoUrl, sizes: '512x512', purpose: 'any' });
            if (resolved.faviconUrl) customIcons.push({ src: resolved.faviconUrl, sizes: '64x64', type: 'image/png', purpose: 'any' });
            const icons = (customIcons.length ? customIcons : (base.icons || [])).map(icon => ({
              ...icon, src: new URL(icon.src, customIcons.length ? window.location.origin : manifestSource).href,
            }));
            const content = {
              ...base,
              name: replaceBrandText(base.name || '小影创作智能体', resolved.name),
              short_name: resolved.name,
              icons,
              scope: new URL(base.scope || '/', window.location.origin).href,
              start_url: new URL(publicTeam?.team_id ? `/login?team=${encodeURIComponent(publicTeam.team_id)}` : '/home', window.location.origin).href,
            };
            manifestUrl = URL.createObjectURL(new Blob([JSON.stringify(content)], { type: 'application/manifest+json' }));
            manifest.setAttribute('href', manifestUrl);
          }).catch(() => { /* The normal web experience remains available if the optional manifest fails. */ });
      }
    }

    return () => {
      disposed = true;
      controller.abort();
      document.title = originalTitle;
      originalIcons.forEach(({ icon, href, type }) => {
        if (href === null) icon.removeAttribute('href'); else icon.setAttribute('href', href);
        if (type === null) icon.removeAttribute('type'); else icon.setAttribute('type', type);
      });
      if (description && originalDescription !== null) description.setAttribute('content', originalDescription);
      if (manifest && originalManifest !== null) manifest.setAttribute('href', originalManifest);
      if (manifestUrl) URL.revokeObjectURL(manifestUrl);
    };
  }, [publicTeam?.team_id, resolved.active, resolved.faviconUrl, resolved.logoUrl, resolved.name]);

  return <TeamBrandContext.Provider value={value}>{children}</TeamBrandContext.Provider>;
}

export function useTeamBrand() {
  return useContext(TeamBrandContext);
}

export function BrandText({ children }) {
  return useTeamBrand().text(children);
}

export default TeamBrandContext;
